/*
 * Builds a valid FHIR R4 RiskAssessment resource from a Signal Card result --
 * the interoperability output several competitors (LymeHQ's FHIR+Blue Button work,
 * LymeLess/Ella's "Warrior Report in HL7 FHIR R4") are also pursuing.
 *
 * Scope: this produces a standards-valid, exportable FHIR resource. It does NOT
 * implement real EHR embedding (SMART on FHIR launch, EHR sandbox access) -- that's
 * a separate, much larger effort (see beta-demo-plan.md gap #5).
 *
 * Contained Observations carry the inputs as the assessment's "basis," per the
 * RiskAssessment resource's intended structure. Where no precise LOINC/SNOMED
 * binding exists for a locally-defined concept (e.g. "outdoor occupation exposure"),
 * a plain-text CodeableConcept.text is used -- valid FHIR, honest about not having a
 * formal terminology binding rather than inventing one.
 */

const TIER_TO_QUALITATIVE_RISK = {
  Elevated: { code: 'high', display: 'High likelihood' },
  Moderate: { code: 'moderate', display: 'Moderate likelihood' },
  Low: { code: 'low', display: 'Low likelihood' },
}

export function buildRiskAssessment({ result, symptoms, bite, occ, county, duration }) {
  const now = new Date().toISOString()
  const qualitative = TIER_TO_QUALITATIVE_RISK[result.tier]

  const obsSymptoms = {
    resourceType: 'Observation',
    id: 'obs-symptoms',
    status: 'final',
    code: { text: 'Symptom presentation reported at intake' },
    subject: { reference: '#patient' },
    effectiveDateTime: now,
    valueString: symptoms.length ? symptoms.join(', ') : 'none reported',
  }

  const obsExposure = {
    resourceType: 'Observation',
    id: 'obs-geo-exposure',
    status: 'final',
    code: { text: 'Geographic tickborne-disease exposure tier (source: CDC Tick Data & Tracker)' },
    subject: { reference: '#patient' },
    effectiveDateTime: now,
    valueCodeableConcept: { text: county.tier === 'high' ? 'High' : county.tier === 'mod' ? 'Moderate' : 'Low' },
    note: [{ text: `County/ZIP context: ${county.label} (${county.zip})` }],
  }

  const obsOccupation = {
    resourceType: 'Observation',
    id: 'obs-occupation',
    status: 'final',
    code: { text: 'Outdoor / agricultural occupational exposure (source: BLS/ACS)' },
    subject: { reference: '#patient' },
    effectiveDateTime: now,
    valueBoolean: occ === 'yes',
  }

  const obsBite = {
    resourceType: 'Observation',
    id: 'obs-bite',
    status: 'final',
    code: { text: 'Tick bite recalled by patient' },
    subject: { reference: '#patient' },
    effectiveDateTime: now,
    valueBoolean: bite === 'yes',
  }

  const obsDuration = {
    resourceType: 'Observation',
    id: 'obs-duration',
    status: 'final',
    code: { text: 'Symptom duration, unresolved' },
    subject: { reference: '#patient' },
    effectiveDateTime: now,
    valueString: duration === 'long' ? '>3 months' : duration === 'mid' ? '1-3 months' : '<4 weeks',
  }

  return {
    resourceType: 'RiskAssessment',
    status: 'final',
    contained: [
      { resourceType: 'Patient', id: 'patient' },
      obsSymptoms, obsExposure, obsOccupation, obsBite, obsDuration,
    ],
    subject: { reference: '#patient' },
    occurrenceDateTime: now,
    method: {
      text: 'Signal Card point-of-care Lyme risk score (True Signal Analytics, TOPx HHS Tech Sprint) -- '
        + 'illustrative composite score; non-conversion rate from a synthetic CMS CCW cohort model, pending '
        + 'validation against the real CCW Limited Data Set.',
    },
    basis: [
      { reference: '#obs-symptoms' },
      { reference: '#obs-geo-exposure' },
      { reference: '#obs-occupation' },
      { reference: '#obs-bite' },
      { reference: '#obs-duration' },
    ],
    prediction: [
      {
        outcome: { coding: [{ system: 'http://snomed.info/sct', code: '23502006', display: 'Lyme disease (disorder)' }] },
        qualitativeRisk: {
          coding: [{ system: 'http://terminology.hl7.org/CodeSystem/risk-probability', code: qualitative.code, display: qualitative.display }],
        },
        probabilityDecimal: +(result.conversionPct / 100).toFixed(4),
        rationale: `Composite score ${result.score}/100 (${result.tier}). Non-conversion rate ${result.nonConversionPct}% `
          + `is a synthetic estimate from ccw-analysis/analyze.py, county tier "${county.tier}" (n=${result.model.n}), `
          + `not real Medicare claims. Not a diagnosis -- suggested next step: ${result.nextStep}`,
      },
    ],
    note: [
      { text: 'Generated by Signal Card for demonstration/interoperability-testing purposes. Decision support only, not a diagnostic device. Contains no real patient data -- subject is an unpopulated placeholder resource.' },
    ],
  }
}
